import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ZoomIn, ZoomOut, X, ChevronLeft, ChevronRight, Maximize2, Minimize2, RotateCcw } from "lucide-react";

function Lightbox({ photos, captions, start, onClose }) {
  const [i, setI] = useState(start);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isFull, setIsFull] = useState(false);
  const containerRef = useRef(null);
  const drag = useRef(null);

  const next = () => { setI((v) => (v + 1) % photos.length); resetView(); };
  const prev = () => { setI((v) => (v - 1 + photos.length) % photos.length); resetView(); };
  const resetView = () => { setZoom(1); setPos({ x: 0, y: 0 }); };
  const zoomBy = (f) => setZoom((z) => Math.min(6, Math.max(1, z * f)));

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "+") zoomBy(1.4);
      else if (e.key === "-") zoomBy(1 / 1.4);
    };
    const onFsChange = () => setIsFull(!!document.fullscreenElement);
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (zoom === 1) setPos({ x: 0, y: 0 }); }, [zoom]);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else containerRef.current?.requestFullscreen?.();
  };

  const onWheel = (e) => zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15);
  const onPointerDown = (e) => {
    if (zoom === 1) return;
    drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (drag.current) setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const onPointerUp = () => { drag.current = null; };

  const btn = "h-9 w-9 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors backdrop-blur-sm";

  return createPortal(
    <div ref={containerRef} className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="evidence-lightbox">
      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <p className="text-white/80 text-sm font-medium" data-testid="lightbox-counter">{i + 1} / {photos.length}</p>
        <div className="flex items-center gap-2">
          <button className={btn} onClick={() => zoomBy(1 / 1.4)} title="Reduzir zoom" data-testid="lightbox-zoom-out-btn"><ZoomOut size={16} /></button>
          <button className={btn} onClick={() => zoomBy(1.4)} title="Ampliar zoom" data-testid="lightbox-zoom-in-btn"><ZoomIn size={16} /></button>
          {zoom > 1 && (
            <button className={btn} onClick={resetView} title="Restaurar" data-testid="lightbox-reset-btn"><RotateCcw size={15} /></button>
          )}
          <button className={btn} onClick={toggleFullscreen} title="Tela cheia" data-testid="lightbox-fullscreen-btn">
            {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button className={btn} onClick={onClose} title="Fechar (Esc)" data-testid="lightbox-close-btn"><X size={17} /></button>
        </div>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden min-h-0 px-14"
        onWheel={onWheel} onDoubleClick={() => (zoom === 1 ? setZoom(2.5) : resetView())}>
        <img
          src={photos[i]}
          alt={captions?.[i] || `Evidência ${i + 1}`}
          draggable={false}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
          className={`max-h-full max-w-full object-contain select-none transition-transform duration-100 ${zoom > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})` }}
          data-testid="lightbox-image"
        />
        {photos.length > 1 && (
          <>
            <button className={`${btn} absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11`} onClick={prev} data-testid="lightbox-prev-btn"><ChevronLeft size={22} /></button>
            <button className={`${btn} absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11`} onClick={next} data-testid="lightbox-next-btn"><ChevronRight size={22} /></button>
          </>
        )}
      </div>

      <div className="shrink-0 px-4 pb-3 pt-2 space-y-2">
        {captions?.[i] && <p className="text-center text-white/70 text-xs" data-testid="lightbox-caption">{captions[i]}</p>}
        {photos.length > 1 && (
          <div className="flex gap-1.5 justify-center overflow-x-auto scrollbar-thin pb-1">
            {photos.map((p, k) => (
              <button key={k} onClick={() => { setI(k); resetView(); }}
                className={`h-12 w-12 shrink-0 rounded-md overflow-hidden border-2 transition-all ${k === i ? "border-white opacity-100" : "border-transparent opacity-50 hover:opacity-80"}`}
                data-testid={`lightbox-thumb-${k}`}>
                <img src={p} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export function EvidenceGallery({ photos, captions = [], cols = "grid-cols-3 sm:grid-cols-5", testId = "evidence-gallery" }) {
  const [index, setIndex] = useState(-1);
  if (!photos?.length) return null;
  return (
    <>
      <div className={`grid ${cols} gap-3`} data-testid={testId}>
        {photos.map((p, i) => (
          <button key={i} type="button" onClick={() => setIndex(i)}
            className="group relative aspect-square rounded-lg overflow-hidden border border-border focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid={`${testId}-thumb-${i}`}>
            <img src={p} alt={captions[i] || `Foto ${i + 1}`} loading="lazy"
              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors flex items-center justify-center">
              <ZoomIn size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </button>
        ))}
      </div>
      {index >= 0 && <Lightbox photos={photos} captions={captions} start={index} onClose={() => setIndex(-1)} />}
    </>
  );
}

export function BeforeAfterSlider({ before, after, beforeLabel = "Antes", afterLabel = "Depois", testId = "before-after-slider" }) {
  const [pct, setPct] = useState(50);
  const ref = useRef(null);

  const move = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPct(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
  };
  const onPointerDown = (e) => { e.currentTarget.setPointerCapture(e.pointerId); move(e.clientX); };
  const onPointerMove = (e) => { if (e.buttons === 1) move(e.clientX); };

  return (
    <div ref={ref} onPointerDown={onPointerDown} onPointerMove={onPointerMove}
      className="relative w-full aspect-video max-h-[440px] rounded-lg overflow-hidden border border-border select-none cursor-ew-resize touch-none"
      data-testid={testId}>
      <img src={after} alt={afterLabel} draggable={false} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}>
        <img src={before} alt={beforeLabel} draggable={false} className="w-full h-full object-cover" />
      </div>
      <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_8px_rgba(0,0,0,0.6)]" style={{ left: `${pct}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white text-black flex items-center justify-center shadow-lg"
          data-testid={`${testId}-handle`}>
          <ChevronLeft size={13} className="-mr-0.5" /><ChevronRight size={13} className="-ml-0.5" />
        </div>
      </div>
      <span className="absolute top-2.5 left-2.5 rounded-md bg-red-500/90 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">{beforeLabel}</span>
      <span className="absolute top-2.5 right-2.5 rounded-md bg-emerald-500/90 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">{afterLabel}</span>
    </div>
  );
}
